import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, Clock, ExternalLink, RotateCcw } from "lucide-react";
import { modules } from "@/data/modules";
import { toast } from "sonner";

interface Order {
  id: string;
  user_id: string;
  email: string | null;
  customer_name: string | null;
  plan: string;
  modules: string | null;
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  created_at: string;
  provisioned: boolean;
}

export default function AdminOrders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = async () => {
    const { data } = await supabase
      .from("orders")
      .select("*")
      .order("created_at", { ascending: false });
    setOrders((data as Order[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { fetchOrders(); }, []);

  const toggleProvisioned = async (id: string, current: boolean) => {
    const { error } = await supabase.from("orders").update({ provisioned: !current }).eq("id", id);
    if (error) {
      toast.error("Fehler: " + error.message);
      return;
    }
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, provisioned: !current } : o)));
    toast.success(!current ? "Als bereitgestellt markiert" : "Status zurückgesetzt");
  };

  const getModuleNames = (moduleStr: string | null): string[] => {
    if (!moduleStr) return [];
    const ids = moduleStr.split(",").map((s) => s.trim());
    return ids.map((id) => modules.find((m) => m.id === id)?.name ?? id);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Bestellungen</h1>
      {orders.length === 0 ? (
        <p className="text-muted-foreground">Noch keine Bestellungen vorhanden.</p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Kunde</TableHead>
                <TableHead>E-Mail</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Module</TableHead>
                <TableHead>Stripe</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Aktion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell className="whitespace-nowrap">
                    {new Date(order.created_at).toLocaleDateString("de-DE")}
                  </TableCell>
                  <TableCell>{order.customer_name || "—"}</TableCell>
                  <TableCell className="text-xs">{order.email || "—"}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{order.plan}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {getModuleNames(order.modules).map((name) => (
                        <Badge key={name} variant="outline" className="text-xs">{name}</Badge>
                      ))}
                      {!order.modules && <span className="text-muted-foreground text-xs">Keine</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {order.stripe_subscription_id ? (
                      <a
                        href={`https://dashboard.stripe.com/subscriptions/${order.stripe_subscription_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {order.stripe_subscription_id.slice(0, 16)}…
                      </a>
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    {order.provisioned ? (
                      <Badge className="bg-green-600 text-white"><Check className="h-3 w-3 mr-1" />Bereitgestellt</Badge>
                    ) : (
                      <Badge variant="destructive"><Clock className="h-3 w-3 mr-1" />Offen</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleProvisioned(order.id, order.provisioned)}
                      title={order.provisioned ? "Status zurücksetzen" : "Als bereitgestellt markieren"}
                    >
                      {order.provisioned ? <RotateCcw className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
